import { AssetFetchRequestV1, AssetFileParamsV1, AssetFileQueryV1, AssetRefV1, AssetUploadFieldsV1, IMAGE_MIME_TYPES, IMAGE_UPLOAD_MAX_BYTES, RehostResultV1, } from '../dto/assets.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ASSET, EXAMPLE_PNG, EXAMPLE_REMOTE_IMAGE_URL } from './examples-assets.js';
/**
 * Images in the editor (1.R). An upload is multipart: the image in `file`, the document and block
 * it belongs to in the other fields; over the cap it answers 413 `payload_too_large`, of another
 * type 415 `unsupported_media_type` (both implicit on multipart routes). The server's streaming
 * cap is 1.R's. A trashed document answers 409 `document_trashed`, after the 403 reference refusal
 * and the 404 (1.0b deliverable 16b). The example consumes its rows: its success resolves the
 * demo's open Old Quay `asset_pending` row and stores an `asset` row.
 */
export const assetsUpload = defineRoute({
    id: 'assets.upload',
    method: 'POST',
    path: '/api/assets',
    requestKind: 'multipart',
    upload: { field: 'file', maxBytes: IMAGE_UPLOAD_MAX_BYTES, mimeTypes: IMAGE_MIME_TYPES },
    body: AssetUploadFieldsV1,
    response: { kind: 'json', schema: AssetRefV1, status: 201 },
    errors: { 409: ['document_trashed'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['body.docId'] },
    status: 'frozen',
    owner: '1.R',
    since: 'editor-alpha',
    examples: {
        request: {
            body: { docId: EXAMPLE_IDS.locationOldQuay.id, blockId: EXAMPLE_IDS.blockOldQuayImage.id },
            file: EXAMPLE_PNG,
        },
        response: EXAMPLE_ASSET,
        // Its success resolves the Old Quay's open `asset_pending` row and stores an `asset` row, so
        // the runner gives each success case a fresh clone (deliverable 16b).
        consumes: true,
    },
});
/**
 * Re-host an image from elsewhere (a Notion export, a web page): stored now, or recorded as
 * pending. A refused scheme or address answers 422 `fetch_refused`. A fetch that fails or times
 * out is not an error: it answers `pending` with an `asset_pending` row. A trashed document
 * answers 409 `document_trashed`, after the 403 reference refusal and the 404 and before any
 * resolver call or connection, writing no `asset_pending` row (1.0b deliverable 16b).
 */
export const assetsFetch = defineRoute({
    id: 'assets.fetch',
    method: 'POST',
    path: '/api/assets/fetch',
    body: AssetFetchRequestV1,
    response: { kind: 'json', schema: RehostResultV1 },
    errors: { 409: ['document_trashed'], 422: ['fetch_refused'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['body.docId'] },
    status: 'frozen',
    owner: '1.R',
    since: 'editor-alpha',
    examples: {
        request: {
            body: {
                url: EXAMPLE_REMOTE_IMAGE_URL,
                docId: EXAMPLE_IDS.locationOldQuay.id,
                blockId: EXAMPLE_IDS.blockOldQuayImage.id,
            },
        },
        response: { status: 'hosted', asset: EXAMPLE_ASSET },
        success: {
            skip: 'a re-host succeeds only when the remote image can be fetched, which no seeded database holds',
        },
    },
});
/** An image file, or one of its variants (`thumb`, `w1200`). */
export const assetsFile = defineRoute({
    id: 'assets.file',
    method: 'GET',
    path: '/files/:assetId',
    params: AssetFileParamsV1,
    query: AssetFileQueryV1,
    response: { kind: 'file' },
    auth: 'session',
    smoke: 'read',
    reference: 'none',
    status: 'frozen',
    owner: '1.R',
    since: 'editor-alpha',
    examples: {
        request: {
            params: { assetId: EXAMPLE_IDS.assetQuayEngraving.id },
            query: { variant: 'thumb' },
        },
        response: { contentType: 'image/png', body: '' },
        success: {
            skip: 'serving an image needs its file under the data directory, which a database seed does not hold',
        },
    },
});
//# sourceMappingURL=assets.js.map